import type { RouterOutputs } from "@/trpc";
import {
  classify,
  getNyToday,
  type CredentialRegistry,
  type CredentialDocument,
  type ProviderCredentialSummary,
} from "@/routes/_org/org/bu/$organizationBusinessUnitId/providers/credentials/_components/classify";

const STATUS_PRIORITY = {
  expired: 0,
  missing: 1,
  "expiring-soon": 2,
  current: 3,
} as const;

/** Group the sheet by category while classifying every instance. The sheet
 * renders the actual files once per category; a current license must not hide
 * another expired license when choosing the category's severity. */
export function workspaceLegacySummary({
  provider,
  registry,
}: {
  provider: RouterOutputs["providerWorkspace"]["legacyDetail"]["provider"];
  registry: CredentialRegistry;
}): ProviderCredentialSummary {
  const today = getNyToday();
  const documents = new Map<string, CredentialDocument>();
  for (const credential of provider.credentials) {
    const code = credential.credentialTypeCode;
    const entry = registry[code];
    const status = classify(
      credential.expirationDate,
      today,
      credential.isPresent,
    );
    const existing = documents.get(code);
    if (
      existing &&
      STATUS_PRIORITY[existing.status] <= STATUS_PRIORITY[status]
    ) {
      continue;
    }
    const fileTypeIds =
      entry?.fileTypeIds ?? (entry?.fileTypeId ? [entry.fileTypeId] : []);
    documents.set(code, {
      id: code,
      type: entry?.displayName ?? code,
      status,
      expiresAt: credential.expirationDate,
      fileTypeId: entry?.fileTypeId ?? null,
      fileTypeIds,
      required: credential.required ?? entry?.required ?? false,
    });
  }
  return {
    id: provider.id,
    firstName: provider.firstName ?? "",
    lastName: provider.lastName ?? "",
    role: provider.role ?? "",
    specialty: provider.specialty ?? "",
    isRevealed: provider.isRevealed,
    documents: [...documents.values()],
  };
}
